import { getApiUrl } from "../utils/api";
import {
  useContext,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useChat } from "../context/ChatContext";
import {
  FaBars,
  FaBell,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaTimes,
  FaHistory,
  FaSearch,
  FaChevronDown,
  FaComments,
  FaUsers,
} from "react-icons/fa";
import { getAvatarUrl, DEFAULT_AVATAR_DATA_URL, FALLBACK_AVATAR_SVG } from "../utils/avatar";
import SidebarGroupsSection from "./SidebarGroupsSection";
import PopularTagsSidebar from "./PopularTagsSidebar";

const Navbar = forwardRef(function Navbar(props, ref) {
  const { user, setUser } = useContext(UserContext);
  const {
    notifications,
    notificationUnreadCount,
    notificationsPagination,
    loadingMoreNotifications,
    loadMoreNotifications,
    fetchNotifications,
    patchNotification,
    markNotificationsAsRead,
    markNotificationAsRead,
    clearNotifications,
    unreadCount: chatUnreadCount,
    unreadReady: chatUnreadReady,
    openChatModal,
  } = useChat();

  // Helper function to truncate poem titles in notification messages
  // Only truncates poem-related notifications (those with poemId), not admin notifications
  const truncatePoemTitle = (message, notification, maxChars = 30) => {
    if (!message || typeof message !== 'string') return message;
    
    // Only truncate if this is a poem-related notification (has poemId)
    // Admin notifications don't have poemId, so they won't be truncated
    if (!notification?.poemId) {
      return message;
    }
    
    // Find text after colon (poem title) - typically at the end of the message
    const colonIndex = message.lastIndexOf(':');
    if (colonIndex !== -1) {
      const beforeColon = message.substring(0, colonIndex + 1);
      const afterColon = message.substring(colonIndex + 1).trim();
      
      if (afterColon.length > maxChars) {
        const truncatedTitle = afterColon.substring(0, maxChars) + '...';
        return beforeColon + ' ' + truncatedTitle;
      }
    }
    
    return message;
  };

  // Helper function to truncate poem titles (for search results and suggestions)
  const truncateTitle = (title, maxChars = 20) => {
    if (!title || typeof title !== 'string') return title;
    if (title.length > maxChars) {
      return title.substring(0, maxChars) + '...';
    }
    return title;
  };
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const bellRef = useRef(null);
  const searchTriggerRef = useRef(null);
  const notifDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState({
    poems: [],
    users: [],
    tags: [],
    groups: [],
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const searchInputRef = useRef(null);
  const mobileSearchWrapRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const suggestionsDropdownRef = useRef(null);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [handledInviteIds, setHandledInviteIds] = useState(new Set());
  const [checkedInviteStatuses, setCheckedInviteStatuses] = useState(new Map()); // poemId -> status
  const [checkingInviteStatus, setCheckingInviteStatus] = useState(new Set()); // poemIds being checked
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const stored = localStorage.getItem("searchHistory");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useImperativeHandle(ref, () => ({
    fetchNotifications,
  }));

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!user && storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, [user, setUser]);

  // Check invite statuses when notifications change
  useEffect(() => {
    if (!user?._id || !notifications.length) return;
    
    // Find collaboration invites that haven't been checked
    const uncheckedInvites = notifications
      .filter((n) => 
        typeof n.message === "string" && 
        n.message.includes("invited you to collaborate on:") && 
        n.poemId &&
        !checkedInviteStatuses.has(n.poemId) &&
        !checkingInviteStatus.has(n.poemId)
      )
      .map((n) => n.poemId);
    
    if (uncheckedInvites.length === 0) return;
    
    // Check status for each unique poem
    const uniquePoemIds = [...new Set(uncheckedInvites)];
    
    uniquePoemIds.forEach((poemId) => {
      setCheckingInviteStatus((prev) => {
        const next = new Set(prev);
        next.add(poemId);
        return next;
      });
      
      fetch(getApiUrl(`/api/poems/${poemId}/collaborators`))
        .then((res) => res.json())
        .then((data) => {
          const invite = (data.invites || []).find(
            (i) => i.user?._id === user._id || i.user?.toString() === user._id
          );
          const status = invite?.status || (data.collaborators?.some((c) => c._id === user._id || c._id?.toString() === user._id) ? "accepted" : null);
          
          setCheckedInviteStatuses((prev) => {
            const next = new Map(prev);
            if (status) next.set(poemId, status);
            return next;
          });
          setCheckingInviteStatus((prev) => {
            const next = new Set(prev);
            next.delete(poemId);
            return next;
          });
        })
        .catch(() => {
          setCheckingInviteStatus((prev) => {
            const next = new Set(prev);
            next.delete(poemId);
            return next;
          });
        });
    });
  }, [notifications, user?._id, checkedInviteStatuses, checkingInviteStatus]);

  // Handle clicks outside search dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideSearchInput = searchInputRef.current?.contains(event.target);
      const insideHistory = searchDropdownRef.current?.contains(event.target);
      const insideSuggestions = suggestionsDropdownRef.current?.contains(
        event.target
      );
      if (!insideSearchInput && !insideHistory && !insideSuggestions) {
        setShowSearchHistory(false);
        setShowSuggestions(false);
      }
    };

    if (showSearchHistory || showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showSearchHistory, showSuggestions]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  // Remove duplicate fetchNotifications call since ChatContext handles it
  // useEffect(() => {
  //   if (user) {
  //     fetchNotifications();
  //   }
  // }, [user]);

  useEffect(() => {
    if (!user) {
      setShowSearchPanel(false);
      setShowSuggestions(false);
      setShowSearchHistory(false);
    }
  }, [user]);

  // Fetch suggestions as user types
  useEffect(() => {
    if (!user || search.trim().length === 0) {
      setSuggestions({ poems: [], users: [], tags: [], groups: [] });
      setShowSuggestions(false);
      return;
    }

    // If user is typing a tag (starts with #), show tag suggestions
    const isTagSearch = search.startsWith("#");
    const searchQuery = isTagSearch ? search.slice(1) : search;

    Promise.all([
      fetch(
        getApiUrl(`/api/poems?search=${encodeURIComponent(
          searchQuery
        )}`)
      ).then((res) => res.json()),
      fetch(
        getApiUrl(`/api/user/search?q=${encodeURIComponent(
          searchQuery
        )}`),
        {
          credentials: "include", // Include cookies for authentication
        }
      ).then((res) => res.json()),
      fetch(getApiUrl(`/api/poems/tags/trending?limit=5`))
        .then((res) => res.json())
        .catch(() => []),
      isTagSearch
        ? Promise.resolve([])
        : fetch(
            getApiUrl(`/api/groups/search?q=${encodeURIComponent(
              searchQuery
            )}`),
            { credentials: "include" }
          )
            .then((res) => res.json())
            .catch(() => []),
    ])
      .then(([poemsResp, usersResp, trendingTags, groupsResp]) => {
        // Normalize poems array from API that may return { poems: [...], pagination: {...} }
        const poemsArr = Array.isArray(poemsResp)
          ? poemsResp
          : Array.isArray(poemsResp?.poems)
          ? poemsResp.poems
          : [];

        // Derive matching tags from poems when searching by hashtag
        let tagSuggestions = Array.isArray(trendingTags) ? trendingTags : [];
        if (isTagSearch) {
          const q = searchQuery.trim().toLowerCase();
          const counts = new Map();
          for (const p of poemsArr) {
            if (Array.isArray(p?.tags)) {
              for (const t of p.tags) {
                if (!q || t.includes(q)) {
                  counts.set(t, (counts.get(t) || 0) + 1);
                }
              }
            }
          }
          const derived = Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([tag, count]) => ({ tag, count }));
          if (derived.length > 0) {
            tagSuggestions = derived;
          }
        }

        setSuggestions({
          poems: isTagSearch ? [] : poemsArr.slice(0, 5),
          users: isTagSearch
            ? []
            : Array.isArray(usersResp)
            ? usersResp.slice(0, 5)
            : [],
          tags: tagSuggestions.slice(0, 5),
          groups: isTagSearch
            ? []
            : Array.isArray(groupsResp)
            ? groupsResp.slice(0, 5)
            : [],
        });
        setShowSuggestions(true);
      })
      .catch(() => {
        setSuggestions({ poems: [], users: [], tags: [], groups: [] });
        setShowSuggestions(false);
      });
  }, [search, user]);

  useEffect(() => {
    // Fetch announcements for all users
    fetch(getApiUrl("/api/admin/announcements"))
      .then((res) => res.json())
      .then((data) => setAnnouncements(Array.isArray(data) ? data : []));
  }, []);

  const handleBellClick = async () => {
    setShowProfileDropdown(false);
    const next = !showDropdown;
    setShowDropdown(next);
    // Match mobile: opening notifications marks all as read
    if (next && user && notifications.length > 0) {
      try {
        await markNotificationsAsRead();
        await fetchNotifications();
      } catch {}
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Notifications dropdown
      if (
        showDropdown &&
        notifDropdownRef.current &&
        !notifDropdownRef.current.contains(e.target) &&
        bellRef.current &&
        !bellRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
      // Profile dropdown
      if (
        showProfileDropdown &&
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(e.target) &&
        !e.target.closest(".profile-avatar-dropdown")
      ) {
        setShowProfileDropdown(false);
      }
      // Search panel
      if (
        showSearchPanel &&
        searchTriggerRef.current &&
        !searchTriggerRef.current.contains(e.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target) &&
        (!searchDropdownRef.current ||
          !searchDropdownRef.current.contains(e.target)) &&
        (!suggestionsDropdownRef.current ||
          !suggestionsDropdownRef.current.contains(e.target))
      ) {
        setShowSearchPanel(false);
        setShowSuggestions(false);
        setShowSearchHistory(false);
      }
      // Not closing mobileMenuOpen here by accident!
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown, showProfileDropdown, showSearchPanel]);

  const handleLogout = async () => {
    try {
      // Call logout endpoint to reset lastActive and clear cookie
      await fetch(getApiUrl("/api/auth/logout"), {
        method: "POST",
        credentials: "include", // Include cookies
      });
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      // Always clear local storage and redirect, even if API call fails
      localStorage.removeItem("user");
      setUser(null);
      navigate("/");
    }
  };

  const unreadCount =
    typeof notificationUnreadCount === "number"
      ? notificationUnreadCount
      : notifications.filter((n) => !n.read).length;

  const saveToSearchHistory = (query) => {
    if (!query || !query.trim()) return;
    const trimmedQuery = query.trim();
    setSearchHistory((prev) => {
      // Remove duplicates and add to beginning
      const filtered = prev.filter((item) => item.toLowerCase() !== trimmedQuery.toLowerCase());
      const updated = [trimmedQuery, ...filtered].slice(0, 10); // Keep max 10 items
      try {
        localStorage.setItem("searchHistory", JSON.stringify(updated));
      } catch (err) {
        console.error("Failed to save search history:", err);
      }
      return updated;
    });
  };

  const deleteSearchHistoryByIndex = (index, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSearchHistory((prev) => {
      if (!Array.isArray(prev) || index < 0 || index >= prev.length) return prev;
      const updated = [...prev.slice(0, index), ...prev.slice(index + 1)];
      try {
        localStorage.setItem("searchHistory", JSON.stringify(updated));
      } catch (err) {
        console.error("Failed to update search history:", err);
      }
      return updated;
    });
  };

  const clearAllSearchHistory = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    try {
      localStorage.removeItem("searchHistory");
    } catch (_) {}
    setSearchHistory([]);
  };

  const handleSearchKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!search.trim()) return;
      saveToSearchHistory(search);
      setShowSuggestions(false);
      setShowSearchHistory(false);
      setShowSearchPanel(false);
      setMobileMenuOpen(false); // Close mobile menu when navigating
      navigate(`/?q=${encodeURIComponent(search)}`);
    }
  };

  const handleSuggestionClick = (type, id) => {
    setShowSuggestions(false);
    setShowSearchHistory(false);
    setSearch("");
    setMobileMenuOpen(false); // Close mobile menu when navigating
    if (type === "poem") {
      navigate(`/poems/${id}`);
    } else if (type === "user") {
      navigate(`/users/${id}`);
    } else if (type === "group") {
      navigate(`/groups/${id}`);
    }
  };

  const handleHistoryClick = (historyQuery) => {
    setSearch(historyQuery);
    saveToSearchHistory(historyQuery);
    setShowSuggestions(false);
    setShowSearchHistory(false);
    setMobileMenuOpen(false);
    navigate(`/?q=${encodeURIComponent(historyQuery)}`);
  };

  const handleBlur = (e) => {
    // Check if the related target (element receiving focus) is inside the dropdown
    const relatedTarget = e.relatedTarget;
    if (
      searchInputRef.current === relatedTarget ||
      (searchDropdownRef.current && searchDropdownRef.current.contains(relatedTarget)) ||
      (suggestionsDropdownRef.current && suggestionsDropdownRef.current.contains(relatedTarget))
    ) {
      return; // Don't hide if clicking inside dropdown or input
    }
    setTimeout(() => {
      setShowSuggestions(false);
      setShowSearchHistory(false);
    }, 150); // allow click
  };

  const navLinks = [
    { label: "Home", to: "/" },
    { label: "Writers", to: "/writers" },
    { label: "About", to: "/about" },
  ];

  const isNavActive = (to) => {
    if (to === "/") {
      return location.pathname === "/" || location.pathname === "/poems";
    }
    if (to === "/writers") return location.pathname === "/writers";
    if (to === "/about") return location.pathname === "/about";
    return false;
  };

  const renderSearchDropdowns = () => (
    <>
      {showSearchHistory && searchHistory.length > 0 && !search.trim() && (
        <ul
          ref={searchDropdownRef}
          className="absolute left-0 mt-1 w-full bg-white border border-[#eeeeee] rounded shadow-lg z-50 max-h-60 overflow-y-auto font-sans"
        >
          <li className="px-2 py-2 text-xs text-gray-400 font-semibold border-b flex items-center justify-between sticky top-0 bg-white">
            <span className="flex items-center gap-2">
              <FaHistory className="w-3 h-3" />
              Recent Searches
            </span>
            <button
              onMouseDown={clearAllSearchHistory}
              className="text-[11px] text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
              aria-label="Clear all searches"
            >
              Clear all
            </button>
          </li>
          {searchHistory.map((historyQuery, index) => (
            <li
              key={index}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center justify-between group"
              onMouseDown={() => handleHistoryClick(historyQuery)}
            >
              <span className="flex-1 truncate">{historyQuery}</span>
              <button
                onMouseDown={(e) => deleteSearchHistoryByIndex(index, e)}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity ml-2"
                aria-label={`Delete search: ${historyQuery}`}
              >
                <FaTimes className="w-3 h-3 text-gray-400 hover:text-red-500" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {showSuggestions &&
        (suggestions.poems?.length > 0 ||
          suggestions.users?.length > 0 ||
          suggestions.tags?.length > 0 ||
          suggestions.groups?.length > 0) && (
          <ul
            ref={suggestionsDropdownRef}
            className="absolute left-0 mt-1 w-full bg-white border border-[#eeeeee] rounded shadow-lg z-50 max-h-60 overflow-y-auto font-sans"
          >
            {suggestions.groups?.length > 0 && (
              <>
                <li className="px-4 py-2 text-xs text-gray-400">Groups</li>
                {suggestions.groups.map((group) => (
                  <li
                    key={group._id}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center gap-2"
                    onMouseDown={() => handleSuggestionClick("group", group._id)}
                  >
                    <FaUsers className="w-3.5 h-3.5 text-[#f26522] shrink-0" />
                    <span className="font-medium truncate">{group.name}</span>
                    <span className="text-xs text-gray-400 ml-auto shrink-0">
                      {group.memberCount} member{group.memberCount !== 1 ? "s" : ""}
                    </span>
                  </li>
                ))}
              </>
            )}
            {suggestions.users?.length > 0 && (
              <>
                <li className="px-4 py-2 text-xs text-gray-400">Users</li>
                {suggestions.users.map((u) => (
                  <li
                    key={u._id}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center gap-2"
                    onMouseDown={() => handleSuggestionClick("user", u.username)}
                  >
                    <img
                      src={getAvatarUrl(u.avatar)}
                      alt="avatar"
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                            ? FALLBACK_AVATAR_SVG
                            : DEFAULT_AVATAR_DATA_URL;
                      }}
                    />
                    <span className="font-medium">{u.username}</span>
                    {u.name && (
                      <span className="text-gray-400 ml-1">({u.name})</span>
                    )}
                  </li>
                ))}
              </>
            )}
            {suggestions.poems?.length > 0 && (
              <>
                <li className="px-4 py-2 text-xs text-gray-400">Poems</li>
                {suggestions.poems.map((poem) => (
                  <li
                    key={poem._id}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                    onMouseDown={() => handleSuggestionClick("poem", poem._id)}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{truncateTitle(poem.title)}</span>
                      {poem.tags && poem.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {poem.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-block bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded"
                            >
                              #{tag}
                            </span>
                          ))}
                          {poem.tags.length > 3 && (
                            <span className="text-xs text-gray-400">
                              +{poem.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </>
            )}
            {suggestions.tags?.length > 0 && (
              <>
                <li className="px-4 py-2 text-xs text-gray-400">Popular Tags</li>
                {suggestions.tags.map((tagData, index) => (
                  <li
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center justify-between"
                    onMouseDown={() => {
                      const searchQuery = tagData.tag;
                      saveToSearchHistory(searchQuery);
                      setSearch(`#${tagData.tag}`);
                      setShowSuggestions(false);
                      setShowSearchHistory(false);
                      setShowSearchPanel(false);
                      setMobileMenuOpen(false);
                      navigate(`/?q=${encodeURIComponent(tagData.tag)}`);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[#f26522]">#</span>
                      <span className="font-medium">{tagData.tag}</span>
                    </span>
                    <span className="text-xs text-gray-400">
                      {tagData.count} poem{tagData.count !== 1 ? "s" : ""}
                    </span>
                  </li>
                ))}
              </>
            )}
          </ul>
        )}
    </>
  );

  return (
    <>
      {/* Announcements Banner */}
      {(Array.isArray(announcements) ? announcements : [])
        .filter((a) => !dismissed.includes(a._id))
        .map((a) => (
          <div
            key={a._id}
            className="peer-announcement-banner w-full bg-amber-100 text-amber-900 border border-amber-300 px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 relative z-50 rounded-lg"
          >
            <span className="font-bold">Announcement:</span> {a.title} -{" "}
            {a.message}
            <button
              className="ml-4 text-amber-600 hover:text-amber-800 text-lg font-bold px-2"
              onClick={() => setDismissed((prev) => [...prev, a._id])}
              aria-label="Dismiss announcement"
            >
              &times;
            </button>
          </div>
        ))}
      <header className="peer-navbar relative z-50" role="banner">
        <nav
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-[5rem] flex items-center justify-between relative"
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Left: mobile menu + brand */}
          <div className="flex items-center gap-3 min-w-0 z-10">
            <button
              className="lg:hidden p-1 text-[#333333]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle mobile menu"
            >
              <FaBars className="w-6 h-6" />
            </button>
            <Link to="/" className="min-w-0 peer-navbar-brand">
              <img
                src="/logo.png"
                srcSet="/logo.png 1x, /logo@2x.png 2x"
                alt="PeerPoetry"
                className="peer-navbar-brand-logo"
                width={320}
                height={117}
              />
            </Link>
          </div>

          {/* Center: nav links + search */}
          <div className="peer-navbar-center-nav hidden lg:flex absolute left-1/2 -translate-x-1/2">
            {navLinks.map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className={`peer-navbar-link ${
                  isNavActive(to) ? "peer-navbar-link--active" : ""
                }`}
              >
                {label}
              </Link>
            ))}
            <div ref={searchTriggerRef} className="relative peer-navbar-search-wrap">
              <FaSearch
                className="peer-navbar-search-icon"
                aria-hidden="true"
              />
              <input
                ref={searchInputRef}
                type="search"
                placeholder="Search poems, writers..."
                className="peer-navbar-search-input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShowSearchPanel(true);
                  if (e.target.value.trim()) {
                    setShowSearchHistory(false);
                  } else if (searchHistory.length > 0) {
                    setShowSearchHistory(true);
                  }
                }}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                  setShowSearchPanel(true);
                  if (search && search.trim()) {
                    setShowSuggestions(true);
                  } else if (searchHistory.length > 0) {
                    setShowSearchHistory(true);
                  }
                }}
                onBlur={handleBlur}
                aria-label="Search"
              />
              {showSearchPanel && (search.trim() || searchHistory.length > 0) && (
                <div className="absolute left-0 top-full mt-2 w-72 z-50">
                  {renderSearchDropdowns()}
                </div>
              )}
            </div>
          </div>

          {/* Right: notifications, messages, write, profile */}
          <div className="flex items-center z-10 relative">
            {user && (
              <div className="flex items-center gap-0.5 sm:gap-1">
                <button
                  ref={bellRef}
                  type="button"
                  onClick={handleBellClick}
                  className="relative p-2 text-[#333333] hover:text-[#333333] transition-colors focus:outline-none"
                  aria-label="Notifications"
                  aria-expanded={showDropdown}
                  title="Notifications"
                >
                  <FaBell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 bg-[#f26522] text-white text-[10px] min-w-[1rem] h-4 px-1 flex items-center justify-center rounded-full font-semibold">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDropdown(false);
                    setShowProfileDropdown(false);
                    openChatModal();
                  }}
                  className="relative p-2 text-[#333333] hover:text-[#333333] transition-colors focus:outline-none"
                  aria-label="Messages"
                  title="Messages"
                >
                  <FaComments className="w-5 h-5" />
                  {chatUnreadReady && chatUnreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 bg-[#f26522] text-white text-[10px] min-w-[1rem] h-4 px-1 flex items-center justify-center rounded-full font-semibold">
                      {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            <Link
              to={user ? "/write" : "/login"}
              className="peer-navbar-write-btn hidden lg:inline-flex text-sm sm:text-base px-3 sm:px-5 py-2 ml-1 sm:ml-2"
            >
              Write
            </Link>

            {user ? (
              <div className="relative ml-3 sm:ml-4">
                <button
                  className="flex items-center gap-2 profile-avatar-dropdown focus:outline-none"
                  onClick={() => setShowProfileDropdown((v) => !v)}
                  title="Profile menu"
                  aria-label="Profile menu"
                  aria-expanded={showProfileDropdown}
                >
                  <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={getAvatarUrl(user?.avatar)}
                      alt={`${user?.username || "User"} profile`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                            ? FALLBACK_AVATAR_SVG
                            : DEFAULT_AVATAR_DATA_URL;
                      }}
                    />
                  </span>
                  <FaChevronDown className="w-3 h-3 text-[#333333] hidden sm:block" />
                </button>
                {showProfileDropdown && (
                  <div
                    ref={profileDropdownRef}
                    className="absolute right-0 mt-2 w-48 bg-white border border-[#eeeeee] rounded shadow-lg z-50 font-sans text-sm"
                  >
                    <Link
                      to="/profile"
                      className="w-full block text-left px-4 py-2.5 hover:bg-[#ffffff] text-gray-800"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      Profile
                    </Link>
                    {(user.role === "admin" || user.role === "superadmin") && (
                      <Link
                        to="/admin"
                        className="navbar-admin-desktop-only hidden lg:block w-full text-left px-4 py-2.5 hover:bg-[#ffffff] text-gray-800"
                        onClick={() => setShowProfileDropdown(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    {(user.role === "moderator" ||
                      user.role === "admin" ||
                      user.role === "superadmin") && (
                      <Link
                        to="/moderator"
                        className="navbar-admin-desktop-only hidden lg:block w-full text-left px-4 py-2.5 hover:bg-[#ffffff] text-gray-800"
                        onClick={() => setShowProfileDropdown(false)}
                      >
                        Moderation
                      </Link>
                    )}
                    <button
                      className="w-full text-left px-4 py-2.5 hover:bg-[#ffffff] text-[#f26522] border-t border-[#eeeeee]"
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setTimeout(() => handleLogout(), 100);
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 sm:gap-4 ml-3 sm:ml-4">
                <Link to="/login" className="peer-navbar-link text-sm sm:text-base">
                  Login
                </Link>
                <Link
                  to="/register"
                  className="peer-navbar-link text-sm sm:text-base hidden sm:inline"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </nav>
      </header>
      {/* Mobile Menu (use fixed position and high z-index) */}
      {mobileMenuOpen && (
        <div className="peer-mobile-menu fixed inset-0 bg-[#ffffff] z-[200] lg:hidden peer-navbar overflow-y-auto overscroll-contain">
          <button
            className="peer-mobile-menu__close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close mobile menu"
          >
            &times;
          </button>

          <div className="peer-mobile-menu__inner">
            {user && (
            <div className="peer-mobile-menu__top-row">
                <div
                  ref={mobileSearchWrapRef}
                  className="peer-mobile-menu__search relative w-full"
                >
            <div className="peer-navbar-search-wrap w-full">
              <FaSearch
                className="peer-navbar-search-icon"
                aria-hidden="true"
              />
            <input
              ref={searchInputRef}
              type="search"
              placeholder="Search poems, writers..."
              className="peer-mobile-menu__search-input"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (e.target.value.trim()) {
                  setShowSearchHistory(false);
                } else if (searchHistory.length > 0) {
                  setShowSearchHistory(true);
                }
              }}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => {
                if (search && search.trim()) {
                  setShowSuggestions(true);
                } else if (searchHistory.length > 0) {
                  setShowSearchHistory(true);
                }
              }}
              onBlur={handleBlur}
              aria-label="Search"
            />
            </div>
            {showSearchHistory && searchHistory.length > 0 && !search.trim() && (
              <ul
                ref={searchDropdownRef}
                className="absolute left-0 mt-1 w-full bg-white border rounded shadow z-50 max-h-60 overflow-y-auto"
              >
                <li className="px-2 py-2 text-xs text-gray-400 font-semibold border-b flex items-center justify-between sticky top-0 bg-white">
                  <span className="flex items-center gap-2">
                    <FaHistory className="w-3 h-3" />
                    Recent Searches
                  </span>
                  <button
                    onMouseDown={clearAllSearchHistory}
                    className="text-[11px] text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                    aria-label="Clear all searches"
                  >
                    Clear all
                  </button>
                </li>
                {searchHistory.map((historyQuery, index) => (
                  <li
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center justify-between group"
                    onMouseDown={() => handleHistoryClick(historyQuery)}
                  >
                    <span className="flex-1 truncate">{historyQuery}</span>
                    <button
                      onMouseDown={(e) => deleteSearchHistoryByIndex(index, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity ml-2"
                      aria-label={`Delete search: ${historyQuery}`}
                    >
                      <FaTimes className="w-3 h-3 text-gray-400 hover:text-red-500" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {showSuggestions &&
              (suggestions.poems?.length > 0 ||
                suggestions.users?.length > 0 ||
                suggestions.tags?.length > 0 ||
                suggestions.groups?.length > 0) && (
                <ul
                  ref={suggestionsDropdownRef}
                  className="absolute left-0 mt-1 w-full bg-white border rounded shadow z-50 max-h-60 overflow-y-auto"
                >
                  {suggestions.groups?.length > 0 && (
                    <>
                      <li className="px-4 py-2 text-xs text-gray-400">Groups</li>
                      {suggestions.groups.map((group) => (
                        <li
                          key={group._id}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center gap-2"
                          onMouseDown={() =>
                            handleSuggestionClick("group", group._id)
                          }
                        >
                          <FaUsers className="w-3.5 h-3.5 text-[#f26522] shrink-0" />
                          <span className="font-medium truncate">{group.name}</span>
                          <span className="text-xs text-gray-400 ml-auto shrink-0">
                            {group.memberCount} member{group.memberCount !== 1 ? "s" : ""}
                          </span>
                        </li>
                      ))}
                    </>
                  )}
                  {suggestions.users?.length > 0 && (
                    <>
                      <li className="px-4 py-2 text-xs text-gray-400">Users</li>
                      {suggestions.users.map((user) => (
                        <li
                          key={user._id}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center gap-2"
                          onMouseDown={() =>
                            handleSuggestionClick("user", user.username)
                          }
                        >
                          {user.avatar && (
                            <img
                              src={
                                user.avatar.startsWith("http")
                                  ? user.avatar
                                  : getApiUrl(`/uploads/${user.avatar}`)
                              }
                              alt="avatar"
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          )}
                          <span className="font-medium">{user.username}</span>
                          {user.name && (
                            <span className="text-gray-400 ml-1">
                              ({user.name})
                            </span>
                          )}
                        </li>
                      ))}
                    </>
                  )}
                  {suggestions.poems?.length > 0 && (
                    <>
                      <li className="px-4 py-2 text-xs text-gray-400">Poems</li>
                      {suggestions.poems.map((poem) => (
                        <li
                          key={poem._id}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                          onMouseDown={() =>
                            handleSuggestionClick("poem", poem._id)
                          }
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{truncateTitle(poem.title)}</span>
                            {poem.tags && poem.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {poem.tags.slice(0, 3).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-block bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                                {poem.tags.length > 3 && (
                                  <span className="text-xs text-gray-400">
                                    +{poem.tags.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </>
                  )}
                  {suggestions.tags?.length > 0 && (
                    <>
                      <li className="px-4 py-2 text-xs text-gray-400">
                        Popular Tags
                      </li>
                      {suggestions.tags.map((tagData, index) => (
                        <li
                          key={index}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center justify-between"
                          onMouseDown={() => {
                            setSearch(`#${tagData.tag}`);
                            setShowSuggestions(false);
                            setMobileMenuOpen(false); // Close mobile menu when navigating
                            navigate(
                              `/?q=${encodeURIComponent(tagData.tag)}`
                            );
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-blue-500">#</span>
                            <span className="font-medium">{tagData.tag}</span>
                          </span>
                          <span className="text-xs text-gray-400">
                            {tagData.count} poem{tagData.count !== 1 ? "s" : ""}
                          </span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              )}
                </div>
            </div>
            )}

            <nav className="peer-mobile-menu__nav" aria-label="Main navigation">
              <ul className="peer-mobile-menu__list">
                {navLinks.map(({ label, to }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className={`peer-mobile-menu__link ${
                        isNavActive(to) ? "peer-mobile-menu__link--active" : ""
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <SidebarGroupsSection
              className="peer-mobile-menu__groups w-full"
              onLinkClick={() => setMobileMenuOpen(false)}
            />

            <PopularTagsSidebar
              className="peer-mobile-menu__tags w-full"
              onLinkClick={() => setMobileMenuOpen(false)}
            />

            <div className="peer-mobile-menu__divider" />

            <div className="peer-mobile-menu__actions">
              <a
                href="https://buymeacoffee.com/imrandesk"
                target="_blank"
                rel="noopener noreferrer"
                className="peer-navbar-coffee-btn"
                onClick={() => setMobileMenuOpen(false)}
              >
                <img
                  src="/images/buy-me-a-coffee-logo.png"
                  alt=""
                  className="h-4 w-4 object-contain"
                  aria-hidden="true"
                />
                <span>Buy me a coffee</span>
              </a>
              {!user && (
                <>
                  <Link
                    to="/login"
                    className="peer-navbar-link text-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="peer-navbar-link text-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Notifications Dropdown */}
      {showDropdown && user && (
        <div
          ref={notifDropdownRef}
          className="absolute z-50 w-[calc(100vw-2rem)] sm:w-80 bg-white border rounded-2xl shadow-2xl max-h-[calc(100vh-8rem)] sm:max-h-96 overflow-y-auto overflow-x-hidden p-0.5"
          style={{
            left: bellRef.current
              ? (() => {
                  const rect = bellRef.current.getBoundingClientRect();
                  const dropdownWidth = window.innerWidth < 640 ? window.innerWidth - 32 : 320;
                  const leftPos = rect.right - dropdownWidth;
                  const minLeft = 16; // Minimum 1rem padding from left edge
                  const maxRight = window.innerWidth - dropdownWidth - 16; // Minimum 1rem padding from right edge
                  if (leftPos < minLeft) return `${minLeft}px`;
                  if (leftPos > maxRight) return `${maxRight}px`;
                  return `${leftPos}px`;
                })()
              : "auto",
            right: "auto",
            top: bellRef.current
              ? `${
                  bellRef.current.getBoundingClientRect().bottom +
                  window.scrollY +
                  8
                }px`
              : "auto",
          }}
        >
          <div className="px-4 py-3 border-b bg-gray-50 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
            <div className="text-lg font-semibold text-gray-800">Notifications</div>
          </div>
          {notifications.length === 0 ? (
            <div className="p-8 text-base text-gray-500 text-center flex flex-col items-center gap-2">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-300">
                <path d="M12 22a2 2 0 0 0 2-2H10a2 2 0 0 0 2 2Zm6-6v-5a6 6 0 0 0-5-5.917V4a1 1 0 1 0-2 0v1.083A6 6 0 0 0 6 11v5l-2 2v1h16v-1l-2-2Z" fill="currentColor"/>
              </svg>
              <div className="text-gray-600">You're all caught up</div>
              <div className="text-xs text-gray-400">New activity will appear here</div>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-100 min-w-0">
                {notifications.map((n, i) => {
                  const notificationId = n._id || n.id || i;
                  return (
                  <li
                    key={notificationId || i}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition flex flex-col gap-1 min-w-0 ${!n.read ? "bg-[#f26522]/5" : ""}`}
                    onClick={async () => {
                      // Mark notification as read when clicked
                      // Try both _id and id fields (MongoDB subdocuments can have either)
                      const notifId = n._id || n.id;
                      await markNotificationAsRead(
                        notifId ? String(notifId) : null,
                        n
                      );
                      setShowDropdown(false);
                      if (n.type === "follow") {
                        // Only navigate if we have a username
                        if (n.from?.username) {
                          navigate(`/users/${n.from.username}`);
                        } else if (n.from?._id) {
                          // If we only have _id, try to navigate to profile or fetch username
                          // For now, just don't navigate if username is missing
                          console.warn("Follow notification missing username:", n);
                        }
                      } else if (
                        (n.type === "like" || n.type === "comment" || n.type === "new_poem") &&
                        n.poemId
                      ) {
                        navigate(`/poems/${n.poemId}`);
                      } else if (
                        (n.type === "mention" ||
                          n.type === "group_discussion_reply" ||
                          n.type === "group_discussion_like" ||
                          n.type === "group_discussion_reply_like") &&
                        n.meta?.groupId
                      ) {
                        const params = new URLSearchParams({ tab: "discussions" });
                        if (n.meta.discussionId) {
                          params.set("discussionId", String(n.meta.discussionId));
                        }
                        navigate(`/groups/${String(n.meta.groupId)}?${params.toString()}`);
                      } else if (
                        (n.type === "reply" || n.type === "mention") &&
                        n.poemId
                      ) {
                        if (n.commentId) {
                          navigate(
                            `/poems/${n.poemId}?commentId=${n.commentId}`
                          );
                        } else {
                          navigate(`/poems/${n.poemId}`);
                        }
                      } else if (
                        (n.type === "group_invite" ||
                          n.type === "group_invite_response" ||
                          n.type === "group_join_request" ||
                          n.type === "group_join_request_response") &&
                        n.meta?.groupId
                      ) {
                        navigate(`/groups/${String(n.meta.groupId)}`);
                      } else if (n.type === "group_poem_share" && n.poemId) {
                        navigate(`/poems/${n.poemId}`);
                      } else if (n.type === "group_poem_share" && n.meta?.groupId) {
                        navigate(`/groups/${String(n.meta.groupId)}`);
                      }
                    }}
                    >
                    <div className="font-medium text-gray-900 flex items-start gap-2 min-w-0 flex-1 w-full">
                      {!n.read && (
                        <span
                          className="mt-1 flex-shrink-0 inline-block w-2 h-2 rounded-full bg-[#f26522]"
                          aria-hidden="true"
                        ></span>
                      )}
                      <div className="min-w-0 flex-1 w-full flex items-start gap-2">
                        {(() => {
                          if (n.type === "follow") return <FaInfoCircle className="text-gray-500 flex-shrink-0 mt-0.5" />;
                          if (n.type === "warning") return <FaExclamationTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" />;
                          if (n.type === "success") return <FaCheckCircle className="text-[#f26522] flex-shrink-0 mt-0.5" />;
                          if (n.type === "error") return <FaTimesCircle className="text-red-500 flex-shrink-0 mt-0.5" />;
                          if (
                            n.type === "collaboration_invite" ||
                            n.type === "collaboration_response" ||
                            n.type === "group_invite" ||
                            n.type === "group_invite_response" ||
                            n.type === "group_join_request" ||
                            n.type === "group_join_request_response" ||
                            n.type === "group_poem_share"
                          )
                            return <FaHistory className="text-gray-500 flex-shrink-0 mt-0.5" />;
                          return <FaBell className="text-gray-500 flex-shrink-0 mt-0.5" />;
                        })()}
                        <span className="min-w-0 flex-1 whitespace-normal break-words">
                          {truncatePoemTitle(n.message, n)}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {new Date(n.date).toLocaleString()}
                    </div>
                    {/* Collaboration invite actions */}
                    {(() => {
                      const isCollaborationInvite = typeof n.message === "string" && n.message.includes("invited you to collaborate on:") && n.poemId;
                      if (!isCollaborationInvite) return null;
                      
                      // Check if we've already handled this notification
                      if (n._id && handledInviteIds.has(n._id)) return null;
                      
                      // Check cached invite status
                      const cachedStatus = checkedInviteStatuses.get(n.poemId);
                      
                      // Don't show buttons if status is accepted, declined, or currently checking
                      if (cachedStatus === "accepted" || cachedStatus === "declined") {
                        return null;
                      }
                      
                      // If currently checking, don't show buttons yet
                      if (checkingInviteStatus.has(n.poemId)) {
                        return null;
                      }
                      
                      // If cached and not pending, don't show
                      if (cachedStatus && cachedStatus !== "pending") {
                        return null;
                      }
                      
                      // Show buttons if:
                      // 1. Status is cached as "pending", OR
                      // 2. Status is not cached yet (default to showing if it's a collaboration invite notification)
                      
                      return (
                        <div className="mt-2 flex gap-2">
                          <button
                            className="text-xs px-2 py-1 bg-[#f26522] text-white rounded hover:bg-[#d95518]"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await fetch(getApiUrl(`/api/poems/${n.poemId}/collaborators/accept`), {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  credentials: "include",
                                  body: JSON.stringify({ accept: true })
                                });
                                if (res.ok) {
                                  // Mark invite as handled
                                  setHandledInviteIds((prev) => {
                                    const next = new Set(prev);
                                    if (n._id) next.add(n._id);
                                    return next;
                                  });
                                  // Cache the status
                                  setCheckedInviteStatuses((prev) => {
                                    const next = new Map(prev);
                                    next.set(n.poemId, "accepted");
                                    return next;
                                  });
                                  await fetchNotifications();
                                  navigate(`/poems/${n.poemId}`);
                                }
                              } catch {}
                            }}
                          >
                            Accept
                          </button>
                          <button
                            className="text-xs px-2 py-1 bg-[#fff5ef] text-[#333333] border border-[#eeeeee] rounded hover:bg-[#eeeeee]"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await fetch(getApiUrl(`/api/poems/${n.poemId}/collaborators/accept`), {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  credentials: "include",
                                  body: JSON.stringify({ accept: false })
                                });
                                if (res.ok) {
                                  // Mark invite as handled
                                  setHandledInviteIds((prev) => {
                                    const next = new Set(prev);
                                    if (n._id) next.add(n._id);
                                    return next;
                                  });
                                  // Cache the status
                                  setCheckedInviteStatuses((prev) => {
                                    const next = new Map(prev);
                                    next.set(n.poemId, "declined");
                                    return next;
                                  });
                                  await fetchNotifications();
                                }
                              } catch {}
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      );
                    })()}
                    {/* Group invite actions */}
                    {(() => {
                      const groupId = String(
                        n.meta?.groupId?._id || n.meta?.groupId || ""
                      );
                      const isGroupInvite =
                        n.type === "group_invite" && groupId;
                      if (!isGroupInvite) return null;
                      if (n._id && handledInviteIds.has(n._id)) return null;

                      const inviteStatus = n.meta?.inviteStatus || "pending";
                      if (inviteStatus !== "pending") return null;

                      return (
                        <div className="mt-2 flex gap-2">
                          <button
                            className="text-xs px-2 py-1 bg-[#f26522] text-white rounded hover:bg-[#d95518]"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await fetch(
                                  getApiUrl(`/api/groups/${groupId}/invites/respond`),
                                  {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    credentials: "include",
                                    body: JSON.stringify({ accept: true }),
                                  }
                                );
                                if (res.ok) {
                                  setHandledInviteIds((prev) => {
                                    const next = new Set(prev);
                                    if (n._id) next.add(n._id);
                                    return next;
                                  });
                                  await fetchNotifications();
                                  navigate(`/groups/${groupId}`);
                                }
                              } catch {}
                            }}
                          >
                            Accept
                          </button>
                          <button
                            className="text-xs px-2 py-1 bg-[#fff5ef] text-[#333333] border border-[#eeeeee] rounded hover:bg-[#eeeeee]"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await fetch(
                                  getApiUrl(`/api/groups/${groupId}/invites/respond`),
                                  {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    credentials: "include",
                                    body: JSON.stringify({ accept: false }),
                                  }
                                );
                                if (res.ok) {
                                  setHandledInviteIds((prev) => {
                                    const next = new Set(prev);
                                    if (n._id) next.add(n._id);
                                    return next;
                                  });
                                  await fetchNotifications();
                                }
                              } catch {}
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      );
                    })()}
                    {/* Group join request actions (creator approves/declines) */}
                    {(() => {
                      const groupId = String(
                        n.meta?.groupId?._id || n.meta?.groupId || ""
                      );
                      const notifId = n._id || n.id;
                      const requesterId = String(
                        n.from?._id || n.from?.id || n.from || ""
                      );
                      const isJoinRequest =
                        n.type === "group_join_request" &&
                        groupId &&
                        requesterId;
                      if (!isJoinRequest) return null;
                      if (notifId && handledInviteIds.has(String(notifId))) {
                        return null;
                      }

                      const requestStatus = n.meta?.requestStatus || "pending";
                      if (requestStatus !== "pending") return null;

                      const markJoinRequestHandled = (status) => {
                        if (notifId) {
                          setHandledInviteIds((prev) => {
                            const next = new Set(prev);
                            next.add(String(notifId));
                            return next;
                          });
                          patchNotification(notifId, {
                            read: true,
                            meta: {
                              ...(n.meta || {}),
                              requestStatus: status,
                            },
                          });
                        }
                      };

                      return (
                        <div className="mt-2 flex gap-2">
                          <button
                            className="text-xs px-2 py-1 bg-[#f26522] text-white rounded hover:bg-[#d95518]"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await fetch(
                                  getApiUrl(`/api/groups/${groupId}/join-requests/respond`),
                                  {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    credentials: "include",
                                    body: JSON.stringify({
                                      userId: requesterId,
                                      accept: true,
                                      notificationId: notifId || undefined,
                                    }),
                                  }
                                );
                                if (res.ok) {
                                  markJoinRequestHandled("accepted");
                                  await fetchNotifications();
                                  window.dispatchEvent(
                                    new CustomEvent("groups-updated", {
                                      detail: { groupId },
                                    })
                                  );
                                  window.dispatchEvent(
                                    new CustomEvent("group-invites-changed", {
                                      detail: { groupId },
                                    })
                                  );
                                  navigate(`/groups/${groupId}`);
                                } else if (res.status === 404) {
                                  markJoinRequestHandled("accepted");
                                  await fetchNotifications();
                                }
                              } catch {}
                            }}
                          >
                            Approve
                          </button>
                          <button
                            className="text-xs px-2 py-1 bg-[#fff5ef] text-[#333333] border border-[#eeeeee] rounded hover:bg-[#eeeeee]"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await fetch(
                                  getApiUrl(`/api/groups/${groupId}/join-requests/respond`),
                                  {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    credentials: "include",
                                    body: JSON.stringify({
                                      userId: requesterId,
                                      accept: false,
                                      notificationId: notifId || undefined,
                                    }),
                                  }
                                );
                                if (res.ok || res.status === 404) {
                                  markJoinRequestHandled("declined");
                                  await fetchNotifications();
                                  window.dispatchEvent(
                                    new CustomEvent("groups-updated", {
                                      detail: { groupId },
                                    })
                                  );
                                  window.dispatchEvent(
                                    new CustomEvent("group-invites-changed", {
                                      detail: { groupId },
                                    })
                                  );
                                }
                              } catch {}
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      );
                    })()}
                  </li>
                  );
                })}
              </ul>
              {notificationsPagination?.hasNextPage && (
                <div className="border-t text-center p-2 bg-white">
                  <button
                    type="button"
                    className="text-xs text-[#f26522] hover:underline font-medium disabled:opacity-60"
                    disabled={loadingMoreNotifications}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await loadMoreNotifications();
                    }}
                  >
                    {loadingMoreNotifications
                      ? "Loading..."
                      : "Load more notifications"}
                  </button>
                </div>
              )}
              <div className="border-t text-center p-3 bg-gray-50 rounded-b-2xl">
                <button
                  className="text-xs text-[#f26522] hover:underline font-medium"
                  onClick={async () => {
                    await clearNotifications();
                    setShowDropdown(false);
                  }}
                >
                  Clear All Notifications
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
});

export default Navbar;
